import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { FundCarryRecipientType, FundManagerType } from '__generated__/graphql';
import { FC, useState } from 'react';

import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel, FormPanelWithReadMode } from 'components/FormPanel';

const AL_INDIA_USER = {
  name: 'AL India',
  userId: '81b3b126-878c-4f0d-a3e7-fef5f8cde7de',
  investmentEntityId: '5cf29564-995c-4b00-a5c4-3c40bd23a7bd',
};

const GET_FUND_CARRY_RECIPIENT = gql(`
  query GetFundCarryRecipients($fundId: ID!) {
  fundCarryRecipients(fundId: $fundId) {
    id
    name
    carryPercentage
    user {
        id
        name
    }
    investmentEntity {
        id
        name
    }
  }
}
`);

const CREATE_FUND_CARRY_RECIPIENT = gql(`
  mutation CreateFundCarryRecipient($fundId: ID!, $carryPercentage: Float!, $userId: ID!, $investmentEntityId: ID!) {
    createFundCarryRecipient(fundId: $fundId, carryPercentage: $carryPercentage, userId: $userId, investmentEntityId: $investmentEntityId) {
      id
    }
  }
`);

const UPDATE_FUND_CARRY_RECIPIENT = gql(`
  mutation UpdateFundCarryRecipient($id: ID!, $carryPercentage: Float!, $userId: ID!, $investmentEntityId: ID!) {
    updateFundCarryRecipient(id: $id, carryPercentage: $carryPercentage, userId: $userId, investmentEntityId: $investmentEntityId) {
      id
    }
  }
`);

const FundRecipients: FC<{ id: string; fundManagers?: FundManagerType[] }> = ({
  id,
  fundManagers,
}) => {
  const { data, loading, error, refetch } = useQuery(GET_FUND_CARRY_RECIPIENT, {
    variables: {
      fundId: id,
    },
  });

  const [createFundCarryRecipient, { loading: createLoading }] = useMutation(
    CREATE_FUND_CARRY_RECIPIENT
  );
  const [updateFundCarryRecipient, { loading: updateLoading }] = useMutation(
    UPDATE_FUND_CARRY_RECIPIENT
  );
  const { showToast, handleToastOpen, handleToastClose } = useToast();
  const {
    showToast: showErrorToast,
    handleToastOpen: handleErrorToastOpen,
    handleToastClose: handleErrorToastClose,
  } = useToast();

  const [editable, setEditable] = useState<
    (FundCarryRecipientType & { userId: string; investmentEntityId: string }) | null
  >(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  if (loading) return <LoadingIndicator />;

  if (error) return <ErrorMessage error={error} refetch={refetch} />;

  const fundCarryRecipients = data?.fundCarryRecipients;

  const fundCarryRecipientsForTable = fundCarryRecipients?.map(recipient => ({
    ...recipient,
    user: recipient.user.name,
    investmentEntity: recipient.investmentEntity.name,
    userId: recipient.user.id,
    investmentEntityId: recipient.investmentEntity.id,
  }));

  const userOptions = [
    {
      value: AL_INDIA_USER.userId,
      label: AL_INDIA_USER.name,
    },
    ...(fundManagers?.map(manager => ({
      value: manager.user.id,
      label: manager.user.name,
    })) || []),
  ];

  return (
    <div className="my-4 border border-gray-300 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Carry Recipients</h3>
        <Button onClick={() => setShowCreate(true)}>Add Recipient</Button>
      </div>

      <DataTable
        data={fundCarryRecipientsForTable || []}
        onClick={value => {
          setEditable(value);
          setSelectedUser(value.userId);
          setShowCreate(false);
        }}
        columns={[
          {
            label: 'User',
            fieldName: 'user',
            type: 'STRING',
          },
          {
            label: 'Investment Entity Name',
            fieldName: 'name',
            type: 'STRING',
          },
          {
            label: 'Carry Percentage',
            fieldName: 'carryPercentage',
            type: 'PERCENTAGE',
          },
          {
            label: 'EDIT',
            type: 'CUSTOM',
            fieldName: 'edit',
            customValue: () => <Button variant="text">Edit</Button>,
          },
        ]}
      />

      {showCreate && (
        <div className="bg-gray-100 mt-4 p-4 rounded-lg">
          <div className="flex justify-between items-center">
            <h3 className="font-medium mb-3">Create Carry Recipient</h3>
            <Button
              variant="text"
              onClick={() => {
                setShowCreate(false);
                setSelectedUser(null);
              }}
            >
              Close
            </Button>
          </div>
          <FormPanel
            loading={createLoading}
            error={null}
            onSubmit={data => {
              createFundCarryRecipient({
                variables: {
                  fundId: id,
                  carryPercentage: data.carryPercentage,
                  userId: data.userId,
                  investmentEntityId: data.investmentEntityId,
                },
              })
                .then(() => {
                  refetch();
                  setShowCreate(false);
                  handleToastOpen();
                })
                .catch(error => {
                  handleErrorToastOpen();
                  console.log(error);
                });
            }}
          >
            <FormInput
              type="number"
              fieldName="carryPercentage"
              label="Carry Percentage"
              defaultValue={0}
              validators={{
                required: true,
              }}
            />
            <FormInput
              type="select"
              fieldName="userId"
              label="User"
              onChange={value => {
                setSelectedUser(value.userId);
              }}
              defaultValue={''}
              options={userOptions}
            />
            {selectedUser ? (
              <FormInput
                type="select"
                fieldName="investmentEntityId"
                label="Investment Entity"
                defaultValue={''}
                options={[
                  {
                    value: AL_INDIA_USER.investmentEntityId,
                    label: AL_INDIA_USER.name,
                  },
                  ...(fundManagers
                    ?.filter(manager => manager.user.id === selectedUser)
                    ?.map(manager => manager.user.investmentEntities)
                    ?.flat()
                    ?.map(entity => ({
                      value: entity?.id,
                      label: entity?.name,
                    })) || []),
                ]}
              />
            ) : (
              <></>
            )}
          </FormPanel>
        </div>
      )}

      {editable && (
        <div className="bg-gray-100 mt-4 p-4 rounded-lg">
          <div className="flex justify-between items-center">
            <h3 className="font-medium mb-3">Edit Carry Recipient</h3>
            <Button
              variant="text"
              onClick={() => {
                setEditable(null);
                setSelectedUser(null);
              }}
            >
              Close
            </Button>
          </div>
          <FormPanel
            loading={updateLoading}
            error={null}
            onSubmit={data => {
              updateFundCarryRecipient({
                variables: {
                  id: editable?.id,
                  carryPercentage: data.carryPercentage,
                  userId: data.userId,
                  investmentEntityId: data.investmentEntityId,
                },
              })
                .then(() => {
                  refetch();
                  setEditable(null);
                  handleToastOpen();
                })
                .catch(error => {
                  console.log(error);
                });
            }}
          >
            <FormInput
              type="select"
              fieldName="userId"
              label="User"
              defaultValue={editable?.userId}
              onChange={value => {
                setSelectedUser(value.userId);
              }}
              options={userOptions}
              validators={{
                required: true,
              }}
            />

            {selectedUser || editable?.userId ? (
              <FormInput
                type="select"
                fieldName="investmentEntityId"
                label="Investment Entity"
                defaultValue={editable?.investmentEntityId}
                options={[
                  ...(fundManagers || []),
                  {
                    user: {
                      id: AL_INDIA_USER.userId,
                      name: AL_INDIA_USER.name,
                      investmentEntities: [
                        {
                          id: AL_INDIA_USER.investmentEntityId,
                          name: AL_INDIA_USER.name,
                        },
                      ],
                    },
                  },
                ]
                  ?.filter(
                    manager =>
                      manager.user.id === selectedUser || manager.user.id === editable?.userId
                  )
                  ?.map(manager => manager.user.investmentEntities)
                  ?.flat()
                  ?.map(entity => ({
                    value: entity?.id,
                    label: entity?.name,
                  }))}
              />
            ) : (
              <></>
            )}
            <FormInput
              type="number"
              fieldName="carryPercentage"
              label="Carry Percentage"
              defaultValue={editable?.carryPercentage}
              validators={{
                required: true,
              }}
            />
          </FormPanel>
        </div>
      )}
      <Toast
        show={showToast}
        handleClose={handleToastClose}
        title="Carry Recipient Added"
        message="Carry Recipient added successfully"
      />
      <Toast
        show={showErrorToast}
        type="error"
        handleClose={handleErrorToastClose}
        title="Error Adding Carry Recipient"
        message="Error adding carry recipient. Please try again."
      />
    </div>
  );
};

export default FundRecipients;
